import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { USER_GET, USER_UPDATE } from '../../api/contracts';
import { getApiErrorMessage } from '../../utils/errorMessage';
import './Page.css';
import './CreateCategory.css';

const AGE_GROUP_OPTIONS = [
  { value: '0-3', label: '0-3' },
  { value: '3-6', label: '3-6' },
  { value: '6+', label: '6+' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function EditUser() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [name, setName] = useState('');
  const [age_group, setAge_group] = useState('3-6');
  const [email, setEmail] = useState('');
  const [google_id, setGoogle_id] = useState('');
  const [is_paid, setIs_paid] = useState(false);
  const [status, setStatus] = useState('active');
  const [phone_number, setPhone_number] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setError('Invalid user.');
      return;
    }
    const id = decodeURIComponent(userId);
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(USER_GET.pathFor(id)), { method: USER_GET.method, headers })
      .then((res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'User not found.' : `Request failed (${res.status}).`);
        return res.json();
      })
      .then((json) => {
        if (cancelled) return;
        const data = json.data || json;
        setUser(data);
        setName(data.name ?? '');
        setAge_group(data.age_group === '<3' ? '0-3' : (data.age_group ?? '3-6'));
        setEmail(data.email ?? '');
        setGoogle_id(data.google_id ?? '');
        setIs_paid(Boolean(data.is_paid));
        setStatus((data.status || 'active').toLowerCase());
        setPhone_number(data.phone_number ?? '');
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load user.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const id = decodeURIComponent(userId);
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const body = {
        name: trimmedName,
        age_group: age_group || undefined,
        email: (email || '').trim() || null,
        google_id: (google_id || '').trim() || null,
        is_paid: is_paid,
        status: status,
      };
      const res = await fetch(apiUrl(USER_UPDATE.pathFor(id)), {
        method: USER_UPDATE.method,
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status));
        setIsSubmitting(false);
        return;
      }
      navigate('/dashboard/user');
    } catch (err) {
      setError(err.message || 'Unable to connect. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page create-category-page">
        <p className="create-category-loading">Loading user…</p>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="page create-category-page">
        <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/user')}>
          <FiArrowLeft className="back-icon" aria-hidden />
          Back to User
        </button>
        <p className="create-category-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="page create-category-page">
      <button type="button" className="create-category-back" onClick={() => navigate('/dashboard/user')}>
        <FiArrowLeft className="back-icon" aria-hidden />
        Back to User
      </button>

      <div className="create-category-header">
        <h3>Edit User</h3>
        <p className="create-category-subtitle">Update user details. Phone number cannot be changed.</p>
      </div>

      <form onSubmit={handleSubmit} className="create-category-form">
        <div className="form-page-layout">
          <div className="form-section">
            <h4 className="form-section-title">Details</h4>
            <div className="form-grid-2">
              <div className="form-row-single">
                <label htmlFor="edit-user-name">Name <span className="label-required" aria-hidden="true">*</span></label>
                <input
                  id="edit-user-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Baabu"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-user-age">Age group</label>
                <select
                  id="edit-user-age"
                  value={age_group}
                  onChange={(e) => setAge_group(e.target.value)}
                  disabled={isSubmitting}
                >
                  {AGE_GROUP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-user-phone">Phone number (read-only)</label>
                <input
                  id="edit-user-phone"
                  type="text"
                  value={phone_number}
                  readOnly
                  disabled
                  style={{ opacity: 0.9 }}
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-user-status">Status</label>
                <select
                  id="edit-user-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={isSubmitting}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-user-email">Email</label>
                <input
                  id="edit-user-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. user@example.com"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label htmlFor="edit-user-google">Google ID</label>
                <input
                  id="edit-user-google"
                  type="text"
                  value={google_id}
                  onChange={(e) => setGoogle_id(e.target.value)}
                  placeholder="e.g. google-oauth-id"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
              </div>
              <div className="form-row-single">
                <label className="form-checkbox-label">
                  <input
                    type="checkbox"
                    checked={is_paid}
                    onChange={(e) => setIs_paid(e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span>Is paid</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="create-category-error" role="alert">{error}</div>}

        <div className="form-actions form-actions-full">
          <button type="button" className="btn-cancel" onClick={() => navigate('/dashboard/user')} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditUser;
